import { useState } from "preact/hooks";
import type { Setting, Texts, TrackingInfoRequest } from "@/type";
import AlertCircleIcon from "./AlertCircleIcon";
import FloatingLabelInput from "./FloatingLabelInput";

type TabType = "order" | "tracking";

/** order_lookup: 1=两种方式均可, 2=仅运单号, 3=仅订单号+联系方式 */
const ORDER_LOOKUP_BOTH = 1;
const ORDER_LOOKUP_TRACKING_NUMBER = 2;
const ORDER_LOOKUP_ORDER_AND_CONTACT = 3;

interface SearchFormProps {
  settings: Setting;
  texts: Texts;
  loading: boolean;
  orderNotFound?: boolean;
  onClearOrderNotFound?: () => void;
  onSearchByOrder: (params: TrackingInfoRequest) => void;
  onSearchByTracking: (params: TrackingInfoRequest) => void;
}

export default function SearchForm({
  settings,
  texts,
  loading,
  orderNotFound = false,
  onClearOrderNotFound,
  onSearchByOrder,
  onSearchByTracking,
}: SearchFormProps) {
  const { style, font_family, order_lookup = ORDER_LOOKUP_BOTH } = settings;
  const { colors, general_content_text_size, borders } = style;

  const [activeTab, setActiveTab] = useState<TabType>("order");
  const [orderNumber, setOrderNumber] = useState("");
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const showTabs = order_lookup === ORDER_LOOKUP_BOTH;
  const showOrderInputs =
    order_lookup === ORDER_LOOKUP_ORDER_AND_CONTACT || (showTabs && activeTab === "order");
  const showTrackingInput =
    order_lookup === ORDER_LOOKUP_TRACKING_NUMBER || (showTabs && activeTab === "tracking");

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    const requiredMsg = "This field is required";
    if (showOrderInputs) {
      if (!orderNumber.trim()) newErrors.orderNumber = requiredMsg;
      if (!emailOrPhone.trim()) newErrors.emailOrPhone = requiredMsg;
    }
    if (showTrackingInput) {
      if (!trackingNumber.trim()) newErrors.trackingNumber = requiredMsg;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTrack = () => {
    if (!validate()) return;
    if (showOrderInputs) {
      onSearchByOrder({ order: orderNumber.trim(), email: emailOrPhone.trim() });
    } else {
      onSearchByTracking({ tracking_number: trackingNumber.trim() });
    }
  };

  const { text_color } = colors;

  const clearOrderNotFound = () => {
    onClearOrderNotFound?.();
  };

  const fieldProps = {
    borderRadius: borders.input_border_corner_radius,
    fontFamily: font_family.font,
    fontSize: general_content_text_size.button_text,
    labelBgColor: colors.general_color.lookup_popup_background,
    borderDefaultColor: colors.general_color.input_field,
    borderFocusColor: text_color.tracking_page_title_text,
    labelIdleColor: text_color.placeholder_text_color,
    labelActiveColor: text_color.tracking_page_title_text,
    inputTextColor: text_color.tracking_page_title_text,
    errorColor: text_color.error_message_text_color,
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center px-4 justify-center"
      style={{
        fontFamily: font_family.font,
      }}
    >
      <div
        id="search-form"
        className="w-full  flex flex-col items-center p-6"
        style={{
          gap: 28,
          background: colors.general_color.lookup_popup_background,
          borderRadius: borders.lookup_popup_corner_radius,
        }}
      >
        <h1
          className="font-bold text-center w-full"
          style={{
            color: colors.text_color.tracking_page_title_text,
            fontSize: general_content_text_size.tracking_page_title_text,
            letterSpacing: "-0.2px",
            lineHeight: "32px",
          }}
        >
          {texts.tracking_page_title}
        </h1>

        <div className="w-full flex flex-col" style={{ gap: 28 }}>
          {/* Tabs */}
          {showTabs && (
            <div className="relative w-full" style={{ height: 52 }}>
              <div
                className="flex gap-2 relative h-full"
                style={{ boxShadow: "inset 0px -1px 0px 0px #e3e3e3" }}
              >
                {(["order", "tracking"] as const).map((tab) => {
                  const isActive = activeTab === tab;
                  const label =
                    tab === "order" ? texts.order_number : texts.shipment_tracking_number;
                  return (
                    <button
                      key={tab}
                      type="button"
                      className="flex-1 flex items-center justify-center relative"
                      style={{
                        color: text_color.tab_text_color,
                        fontSize: general_content_text_size.button_text,
                        fontWeight: 650,
                        fontFamily: font_family.font,
                        height: 52,
                      }}
                      onClick={() => {
                        setActiveTab(tab);
                        clearOrderNotFound();
                      }}
                    >
                      {label}
                      {isActive && (
                        <span
                          className="absolute bottom-0 left-0 right-0"
                          style={{
                            height: 3,
                            borderRadius: 1.5,
                            backgroundColor: colors.general_color.tab || "#131313",
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* 输入区域 */}
          <div>
            <div className="flex flex-col w-full gap-5">
              {showOrderInputs && (
                <>
                  <FloatingLabelInput
                    id="order-number"
                    label={texts.order_number}
                    value={orderNumber}
                    error={errors.orderNumber}
                    onValueChange={(v) => {
                      setOrderNumber(v);
                      setErrors((prev) => ({ ...prev, orderNumber: "" }));
                      clearOrderNotFound();
                    }}
                    {...fieldProps}
                  />
                  <FloatingLabelInput
                    id="email-or-phone"
                    label={texts.your_email_or_phone_number}
                    value={emailOrPhone}
                    error={errors.emailOrPhone}
                    onValueChange={(v) => {
                      setEmailOrPhone(v);
                      setErrors((prev) => ({ ...prev, emailOrPhone: "" }));
                      clearOrderNotFound();
                    }}
                    {...fieldProps}
                  />
                </>
              )}
              {showTrackingInput && (
                <FloatingLabelInput
                  id="tracking-number"
                  label={texts.shipment_tracking_number}
                  value={trackingNumber}
                  error={errors.trackingNumber}
                  onValueChange={(v) => {
                    setTrackingNumber(v);
                    setErrors((prev) => ({ ...prev, trackingNumber: "" }));
                    clearOrderNotFound();
                  }}
                  {...fieldProps}
                />
              )}
            </div>
            {orderNotFound && (
              <div className="flex items-center w-full gap-2 mt-4">
                <AlertCircleIcon />
                <p
                  className="font-medium"
                  style={{
                    color: text_color.error_message_text_color,
                    fontSize: general_content_text_size.shipping_status_text_tip,
                    lineHeight: "20px",
                    fontFamily: font_family.font,
                    fontWeight: 450,
                  }}
                >
                  {texts.order_not_found}
                </p>
              </div>
            )}
          </div>
          <button
            type="button"
            className="w-full flex items-center justify-center transition-opacity disabled:opacity-70"
            style={{
              backgroundColor: colors.general_color.button_background,
              color: colors.text_color.primary_button_text,
              borderRadius: borders.button_corner_radius,
              fontSize: general_content_text_size.button_text,
              fontWeight: 550,
              height: 52,
              lineHeight: "20px",
            }}
            onClick={handleTrack}
            disabled={loading}
          >
            {loading ? (
              <span
                style={{
                  borderColor: `#0000 ${colors.text_color.primary_button_text} ${colors.text_color.primary_button_text}`,
                }}
                className="w-5 h-5 rounded-full animate-spin border-2"
                role="status"
                aria-label="Loading"
              />
            ) : (
              texts.track_button
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
